import analyticsKit, { consoleProvider } from '@assetplan/analytics-kit';
import posthog from 'posthog-js';
import { createFeatureFlagService, createPosthogProvider, createConsoleProvider, FLAG } from './feature-flags';

const bootstrapSitioWebAnalytics = function() {
    posthog.init(import.meta.env.VITE_POSTHOG_SITIO_WEB_KEY, {
        api_host: 'https://p.assetplan.cl', // your managed reverse proxy domain
        ui_host: 'https://us.posthog.com', // necessary because you're using a proxy, this way links will point back to PostHog properly
        defaults: '2026-01-30',
        person_profiles: 'identified_only',
    }, 'sitio_web');

    const posthogSitioWebProvider = {
        identified: false,
        identify: function(userId, traits) {
            if (this.identified) {
                console.log('already identified');
                return;
            }
            posthog.sitio_web.identify(userId, traits);
            this.identified = true;
        },
        track: function(eventName, eventProperties) {
            posthog.sitio_web.capture(eventName, eventProperties);
        },
    }


    return analyticsKit.registerProvider(posthogSitioWebProvider);
}

const bootstrapConsoleAnalytics = function() {
    const ourConsoleProvider = {
        identified: false,
        identify: function(userId, traits) {
            if (this.identified) {
                console.log('already identified');
                return;
            }
            consoleProvider.identify(userId, traits);
            this.identified = true;
        },
        track: function(eventName, eventProperties) {
            consoleProvider.track(eventName, eventProperties);
        }
    }
    return analyticsKit.registerProvider(ourConsoleProvider);
}

const bootstrapSessionlessAnalytics = function() {
    posthog.init(import.meta.env.VITE_POSTHOG_SESSIONLESS_KEY, {
        api_host: 'https://p.assetplan.cl', // your managed reverse proxy domain
        ui_host: 'https://us.posthog.com', // necessary because you're using a proxy, this way links will point back to PostHog properly
        defaults: '2026-01-30',
        person_profiles: 'identified_only',
        persistence: 'localStorage+cookie',
        cookie_expiration: 365,
        cross_subdomain_cookie: true,
        cookie_domain: '.assetplan.cl',
    }, 'sessionless');

    const posthogSessionlessProvider = {
        identified: false,
        identify: function(userId, traits) {
            if (this.identified) {
                console.log('already identified');
                return;
            }
            posthog.sessionless.identify(userId, traits);
            this.identified = true;
        },
        track: function(eventName, eventProperties) {
            posthog.sessionless.capture(eventName, eventProperties);
        },
    }

    return analyticsKit.registerProvider(posthogSessionlessProvider);
}

/**
 * Feature flags usando la instancia posthog 'sitio_web' (producción)
 * o el console provider que loguea cada llamada sin enviar datos (desarrollo).
 *
 * Override local-only: en dev/staging, si VITE_ABC_VARIANT_OVERRIDE está
 * definida, devuelve esa variante cuando se consulte el flag del experimento
 * A/B/C de agenda. Sirve para validar visualmente cada variante sin tener
 * que tocar PostHog.
 */
const bootstrapFeatureFlags = function (isProduction = true) {
    const baseProvider = isProduction
        ? createPosthogProvider(posthog.sitio_web)
        : createConsoleProvider();

    if (isProduction) {
        return createFeatureFlagService(baseProvider);
    }

    // Override local-only del experimento A/B/C de agenda.
    // La referencia via window impide que Rollup/tree-shake elimine el bloque
    // cuando VITE_ABC_VARIANT_OVERRIDE se resuelve a undefined en build.
    const override = (typeof window !== 'undefined' && window.__ABC_VARIANT_OVERRIDE__)
        ?? import.meta.env.VITE_ABC_VARIANT_OVERRIDE;
    const experiment = FLAG.ABC_TEST_NUEVO_FLUJO_DE_AGENDA;
    const hasValidOverride = [
        experiment.CONTROL,
        experiment.HORARIO_VISIBLE,
    ].includes(override);

    const wrapped = {
        onReady: (cb) => baseProvider.onReady(cb),
        isEnabled: (flag) => flag === experiment.key && hasValidOverride
            ? true
            : baseProvider.isEnabled(flag),
        getVariant: (flag, fallback) => {
            if (flag === experiment.key && hasValidOverride) {
                console.log(`[FeatureFlags] override → "${override}" for ${experiment.key}`);
                return override;
            }
            return baseProvider.getVariant(flag, fallback);
        },
    };
    return createFeatureFlagService(wrapped);
};

export { bootstrapSitioWebAnalytics, bootstrapConsoleAnalytics, bootstrapSessionlessAnalytics, bootstrapFeatureFlags };
